import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Grid2';

import { SpellList } from "@dndModels";
import { CHARACTER_PIECES_v1, CHARACTER_PIECES_v2, CHARACTER_PIECES_v3 } from "@dndModels/Constants";
import { DataType } from '@dndModels/Types';

import API from "@utilities/smart_api";
import {
  SpellListGroup, countIdRefs, groupSpellLists, mergeSpellListDocs, normalizeName, objectIdTime, replaceIdRefs,
} from '../../utilities/spell_list_merge';

import LoadingBox from "@components/input/LoadingBox";
import SelectStringBox from '@components/input/selects/SelectStringBox';
import CheckBox from '@components/input/CheckBox';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();

// Everything that can store a spell list id somewhere inside it (list_ids on spell limits,
// spell_list_ids on spellcasting features/modifiers/spells, spellbook spell_list, character
// spell_list_id / extra_spell_lists, ...). replaceIdRefs matches on the id itself, so the
// field names don't need to be listed.
const CONTENT_COLLECTIONS: DataType[] = [
  "floating_feature", "creature_feature", "standard_features", "optional_feature_base",
  "spell", "magic_item_keyword", "item", "game_class", "subclass", "race", "subrace", "background",
  "feat", "gift", "eldritch_invocation", "pact_boon", "fighting_style", "special_feature",
  "bastion_facility", "condition", "creature", "creature_gift", "tool", "template",
];
const CHARACTER_COLLECTIONS: string[] = ["character", ...Array.from(new Set(
  [...CHARACTER_PIECES_v1, ...CHARACTER_PIECES_v2, ...CHARACTER_PIECES_v3].filter(p => p !== "main")
)).map(p => `character_${p}`)];
const LIGHT_CHUNK_SIZE = 200;

// A doc that might reference a spell list. Content collections keep a _light copy too.
interface RefDoc { collection: string, data_type: DataType, doc: any, has_light: boolean }
type OrphanAction = "restore" | "delete";
interface Plan {
  key: string;
  name: string;
  fulls: any[];
  lights: any[];
  ids: string[];
  link_groups: number;
  keeper_id: string; // "" when there's no full doc left
  orphan_action: OrphanAction | null; // only when there's no full doc
  refs: number;
}
interface RunStatus { step: string, done: number, total: number, failed: string[], finished: boolean, notes: string[] }

// Every document in a collection, straight from the database (no models, no source book filtering).
const fetchRawCollection = async (data_type: DataType, collection: string = data_type, filter: any = {}) => {
  const count = await api.getObjectCountFromAPIRaw({ collection, filter });
  if (!count || count.count === undefined || count.count < 0) {
    throw new Error(`Couldn't count ${collection}`);
  }
  const docs = count.count > 0 ? await api.getAllObjectsFromAPIRaw({ data_type, collection, filter }) : [];
  if (docs.length < count.count) {
    throw new Error(`Only got ${docs.length} of ${count.count} ${collection}`);
  }
  return docs as any[];
};

// Extended JSON, so a Compass import restores _id as an ObjectId rather than a string.
const downloadAsExtendedJson = (file_name: string, docs: any[]) => {
  const ejson = docs.map(doc => ({ ...doc, _id: { "$oid": doc._id } }));
  const blob = new Blob([JSON.stringify(ejson, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = file_name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// $sets only the given fields. property_names matters: without it the server's
// updateObject inserts the (partial) doc when the _id doesn't match anything.
const patchFields = async (collection: string, _id: string, fields: Record<string, any>) => {
  const property_names = Object.keys(fields).filter(k => k !== "_id");
  const res = await api.patchData({ system: "D&D", path: "/updateObject", data: { data_type: collection, obj: { ...fields, _id }, property_names } });
  return res?.success === true;
};

const createDoc = async (collection: string, obj: any): Promise<string> => {
  const res = await api.postData({ system: "D&D", path: "/createObject", data: { data_type: collection, obj } });
  if (!res || typeof res.id !== "string" || res.id === "") {
    throw new Error(`Couldn't create in ${collection}`);
  }
  return res.id;
};

// Raw doc, not a ModelBase, so deleteObject doesn't go looking for a light copy of it.
const deleteDoc = async (collection: string, _id: string) => {
  const res = await api.deleteObject(collection as DataType, { _id } as any);
  return !!res?.body?.message?.includes("deleted!");
};

const sbName = (sb_id: string) => {
  const book = api.source_books.find(o => o._id === sb_id);
  return book ? (book.abbreviation || book.name) : sb_id;
};

const docSBIds = (doc: any): string[] => ((doc.source_books ?? []) as any[]).map(sb => sb?.id).filter((id: any) => typeof id === "string");

const groupSBKey = (group: SpellListGroup) =>
  Array.from(new Set([...group.fulls, ...group.lights].flatMap(docSBIds))).sort().join(",");

const created = (id: string) => {
  const t = objectIdTime(id);
  return t ? new Date(t * 1000).toISOString().slice(0, 10) : "?";
};

export default function SpellListRepair() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState("Loading Spell Lists");
  const [fulls, setFulls] = useState<any[]>([]);
  const [lights, setLights] = useState<any[]>([]);
  const [ref_docs, setRefDocs] = useState<RefDoc[]>([]);
  // Same-named lists that aren't linked by id: name -> merge them?
  const [name_merges, setNameMerges] = useState<Record<string, boolean>>({});
  const [keepers, setKeepers] = useState<Record<string, string>>({});
  const [orphan_actions, setOrphanActions] = useState<Record<string, OrphanAction>>({});
  const [skipped, setSkipped] = useState<string[]>([]);
  const [show_healthy, setShowHealthy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [status, setStatus] = useState<RunStatus | null>(null);
  const mounted = useRef(false);

  const loadSpellLists = useCallback(async () => {
    const [full_docs, light_docs] = await Promise.all([
      fetchRawCollection("spell_list"),
      fetchRawCollection("spell_list", "spell_list_light"),
    ]);
    return { full_docs, light_docs };
  }, []);

  useEffect(() => {
    mounted.current = true;
    const load = async () => {
      try {
        const { full_docs, light_docs } = await loadSpellLists();
        if (!mounted.current) {
          return;
        }
        setFulls(full_docs);
        setLights(light_docs);
        setLoading("Loading everything that can reference a Spell List");
        // One bad collection shouldn't block the whole repair, but it has to be visible:
        // anything in it that references a duplicate won't be repointed.
        const skipped: string[] = [];
        const tolerant = (data_type: DataType, collection: string) => fetchRawCollection(data_type, collection).catch(ex => {
          console.error(ex);
          skipped.push(collection);
          return [] as any[];
        });
        const content = await Promise.all(CONTENT_COLLECTIONS.map(data_type => tolerant(data_type, data_type)));
        const characters = await Promise.all(CHARACTER_COLLECTIONS.map(collection => tolerant("character", collection)));
        setSkipped(skipped);
        if (!mounted.current) {
          return;
        }
        setRefDocs([
          ...CONTENT_COLLECTIONS.flatMap((data_type, i) => content[i].map(doc => ({ collection: data_type, data_type, doc, has_light: true }))),
          ...CHARACTER_COLLECTIONS.flatMap((collection, i) => characters[i].map(doc => ({ collection, data_type: "character" as DataType, doc, has_light: false }))),
        ]);
      } catch (ex: any) {
        console.error(ex);
        if (mounted.current) {
          setError(ex?.message ?? "Failed to load");
        }
      }
      if (mounted.current) {
        setLoading("");
      }
    };
    load();
    return () => {
      mounted.current = false;
    };
  }, [loadSpellLists]);

  const link_groups = useMemo(() => groupSpellLists(fulls, lights), [fulls, lights]);

  // Spell lists reference each other through list_ids, so the healthy ones are scanned too.
  const all_ref_docs = useMemo((): RefDoc[] => [
    ...ref_docs,
    ...link_groups.filter(g => g.healthy).map(g => ({ collection: "spell_list", data_type: "spell_list" as DataType, doc: g.fulls[0], has_light: true })),
  ], [ref_docs, link_groups]);

  const ref_counts = useMemo(() =>
    countIdRefs(all_ref_docs.map(r => r.doc), link_groups.flatMap(g => g.ids)),
  [all_ref_docs, link_groups]);

  // Link groups that share a name. They merge by default only when they cover the same
  // source books, since e.g. a 2014 and a 2024 Wizard list can legitimately both exist.
  const name_clusters = useMemo(() => {
    const clusters = new Map<string, SpellListGroup[]>();
    link_groups.forEach(g => {
      const name = normalizeName(g.name);
      clusters.set(name, [...(clusters.get(name) ?? []), g]);
    });
    return clusters;
  }, [link_groups]);

  const nameMerge = (name: string, groups: SpellListGroup[]) =>
    name_merges[name] ?? new Set(groups.map(groupSBKey)).size === 1;

  const plans = useMemo((): Plan[] => {
    const result: Plan[] = [];
    name_clusters.forEach((groups, name) => {
      const sets = groups.length > 1 && nameMerge(name, groups) ? [groups] : groups.map(g => [g]);
      for (const set of sets) {
        const key = set.map(g => g.key).sort().join("|");
        const plan_fulls = set.flatMap(g => g.fulls);
        const plan_lights = set.flatMap(g => g.lights);
        const ids = Array.from(new Set(set.flatMap(g => g.ids)));
        const refs = ids.reduce((n, id) => n + (ref_counts.get(id) ?? 0), 0);
        // Default keeper: the full doc most things point at, then the oldest (the original).
        const default_keeper = [...plan_fulls].sort((a, b) =>
          (ref_counts.get(b._id) ?? 0) - (ref_counts.get(a._id) ?? 0) || objectIdTime(a._id) - objectIdTime(b._id))[0];
        const keeper_id = plan_fulls.some(f => f._id === keepers[key]) ? keepers[key] : (default_keeper?._id ?? "");
        result.push({
          key, name: set[0].name, fulls: plan_fulls, lights: plan_lights, ids, link_groups: set.length, keeper_id, refs,
          orphan_action: plan_fulls.length > 0 ? null : (orphan_actions[key] ?? (refs > 0 ? "restore" : "delete")),
        });
      }
    });
    return result.sort((a, b) => a.name.localeCompare(b.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name_clusters, name_merges, keepers, orphan_actions, ref_counts]);

  const needsWork = (plan: Plan) => plan.link_groups > 1 ||
    !(plan.fulls.length === 1 && plan.lights.length === 1 && link_groups.find(g => g.key === plan.key)?.healthy);
  const work = useMemo(() => plans.filter(needsWork),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [plans, link_groups]);

  // Old id -> kept id. Restored lists don't have an id yet, so they map to a placeholder
  // that's swapped for the new id at run time.
  const planned_id_map = useMemo(() => {
    const map = new Map<string, string>();
    for (const plan of work) {
      if (plan.orphan_action === "delete") {
        continue;
      }
      const to = plan.keeper_id || `restore:${plan.key}`;
      plan.ids.filter(id => id !== plan.keeper_id).forEach(id => map.set(id, to));
    }
    return map;
  }, [work]);

  const work_ids = useMemo(() => new Set(work.flatMap(p => p.ids)), [work]);
  const targets = useMemo(() => all_ref_docs
    .filter(r => !(r.collection === "spell_list" && work_ids.has(r.doc._id)))
    .map(r => ({ ...r, changes: replaceIdRefs(r.doc, planned_id_map) }))
    .filter(r => r.changes.count > 0),
  [all_ref_docs, planned_id_map, work_ids]);

  const dangling = work.filter(p => p.orphan_action === "delete" && p.refs > 0);

  if (!api.user || !api.user.admin) {
    return <div className="centered">Admins only.</div>;
  }
  if (error) {
    return <div className="centered">{ error }</div>;
  }
  if (loading) {
    return (
      <div className="centered">
        <LoadingBox message={loading} size={window.innerHeight * 0.5} height={window.innerHeight - 81} />
      </div>
    );
  }

  const running = !!status && !status.finished;

  const run = async () => {
    setConfirm(false);
    const failed: string[] = [];
    const notes: string[] = [];
    const update = (step: string, done: number, total: number, finished = false) => {
      if (mounted.current) {
        setStatus({ step, done, total, failed: [...failed], finished, notes: [...notes] });
      }
    };
    const run_plans = [...work];
    const run_targets = [...targets];
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");

    // 1. Backup. Nothing changes if this fails.
    const target_lights = new Map<string, any>(); // full _id -> its light doc
    try {
      update("Backing up", 0, 0);
      for (const t of run_targets.filter(t => t.has_light && t.collection === "spell_list")) {
        const light = lights.find(l => l._id === t.doc.light_id);
        if (light) {
          target_lights.set(t.doc._id, light);
        }
      }
      const by_collection = new Map<string, typeof run_targets>();
      run_targets.forEach(t => by_collection.set(t.collection, [...(by_collection.get(t.collection) ?? []), t]));
      for (const [collection, list] of Array.from(by_collection.entries())) {
        downloadAsExtendedJson(`spell_list_repair_${stamp}_${collection}.json`, list.map(t => t.doc));
        if (collection === "spell_list" || !list[0].has_light) {
          continue;
        }
        const light_ids = list.map(t => t.doc.light_id).filter((id: any) => typeof id === "string" && id !== "");
        const light_docs: any[] = [];
        for (let i = 0; i < light_ids.length; i += LIGHT_CHUNK_SIZE) {
          light_docs.push(...await fetchRawCollection(list[0].data_type, `${collection}_light`, { "_id*ObjectIDs": light_ids.slice(i, i + LIGHT_CHUNK_SIZE) }));
        }
        const by_light_id = new Map(light_docs.map(l => [l._id, l]));
        list.forEach(t => {
          const light = by_light_id.get(t.doc.light_id);
          if (light) {
            target_lights.set(t.doc._id, light);
          }
        });
        if (light_docs.length > 0) {
          downloadAsExtendedJson(`spell_list_repair_${stamp}_${collection}_light.json`, light_docs);
        }
      }
      downloadAsExtendedJson(`spell_list_repair_${stamp}_spell_list_groups.json`, run_plans.flatMap(p => p.fulls));
      downloadAsExtendedJson(`spell_list_repair_${stamp}_spell_list_light_groups.json`, run_plans.flatMap(p => p.lights));
    } catch (ex: any) {
      console.error(ex);
      failed.push(`Backup failed, so nothing was changed: ${ex?.message ?? ex}`);
      update("Backup", 0, 0, true);
      return;
    }

    // 2. Write each kept list (merged content + a fresh, correct light copy).
    const writes = run_plans.filter(p => p.orphan_action !== "delete");
    const resolved = new Map<string, string>(); // restore placeholder -> new id
    const new_light_ids = new Set<string>();
    let done = 0;
    for (const plan of writes) {
      try {
        const members = [...plan.fulls, ...plan.lights];
        const keeper_doc = plan.fulls.find(f => f._id === plan.keeper_id) ?? plan.lights[0];
        const merged = mergeSpellListDocs(members, keeper_doc, sbName);
        merged.conflicts.forEach(c => notes.push(`${plan.name}: ${c}`));
        const raw: any = { ...keeper_doc, ...merged.fields };
        delete raw.true_id;
        delete raw.light_id;
        let keeper_id = plan.keeper_id;
        if (keeper_id === "") {
          keeper_id = await createDoc("spell_list", { ...raw, _id: "" });
          resolved.set(`restore:${plan.key}`, keeper_id);
          notes.push(`${plan.name}: restored as a new spell list (${keeper_id})`);
        }
        const model = new SpellList(raw);
        model._id = keeper_id;
        model.light_id = "";
        // Lists in the group that pointed at each other now point at the kept one; drop the self-reference.
        const self_map = new Map(plan.ids.filter(id => id !== keeper_id).map(id => [id, keeper_id]));
        for (const sb_id of model.limit_sbids) {
          const limit = model.limit_hash[sb_id];
          limit.list_ids = Array.from(new Set(limit.list_ids.map(id => self_map.get(id) ?? id))).filter(id => id !== keeper_id);
        }
        model.light_id = await createDoc("spell_list_light", model.toLight());
        new_light_ids.add(model.light_id);
        if (!await patchFields("spell_list", keeper_id, model.toDBObj())) {
          failed.push(`${plan.name}: saving the merged list`);
        }
      } catch (ex: any) {
        console.error(ex);
        failed.push(`${plan.name}: ${ex?.message ?? ex}`);
      }
      update("Saving merged spell lists", ++done, writes.length);
    }
    if (failed.length > 0) {
      failed.push("Stopped before repointing references or deleting anything");
      update("Saving merged spell lists", done, writes.length, true);
      return;
    }

    // 3. Repoint everything that referenced a removed copy.
    const id_map = new Map(Array.from(planned_id_map.entries()).map(([from, to]) => [from, resolved.get(to) ?? to]));
    const light_collection = (t: RefDoc) => `${t.collection}_light`;
    done = 0;
    for (const t of run_targets) {
      try {
        const changes = replaceIdRefs(t.doc, id_map);
        if (!await patchFields(t.collection, t.doc._id, changes.fields)) {
          failed.push(`${t.doc.name ?? t.doc._id} (${t.collection})`);
        }
        const light = target_lights.get(t.doc._id);
        const light_changes = light ? replaceIdRefs(light, id_map) : null;
        if (light && light_changes && light_changes.count > 0 && !await patchFields(light_collection(t), light._id, light_changes.fields)) {
          failed.push(`${t.doc.name ?? t.doc._id} (${light_collection(t)})`);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(`${t.doc.name ?? t.doc._id} (${t.collection})`);
      }
      update("Repointing references", ++done, run_targets.length);
    }
    if (failed.length > 0) {
      failed.push("Didn't delete any duplicates, so nothing is left pointing at a missing list. Fix these and run it again.");
      update("Repointing references", done, run_targets.length, true);
      return;
    }

    // 4. Delete the duplicates and old light copies.
    const deletes: Array<{ collection: string, _id: string, name: string }> = [];
    for (const plan of run_plans) {
      plan.fulls.filter(f => plan.orphan_action === "delete" || f._id !== plan.keeper_id)
        .forEach(f => deletes.push({ collection: "spell_list", _id: f._id, name: plan.name }));
      plan.lights.filter(l => !new_light_ids.has(l._id))
        .forEach(l => deletes.push({ collection: "spell_list_light", _id: l._id, name: plan.name }));
    }
    done = 0;
    for (const d of deletes) {
      try {
        if (!await deleteDoc(d.collection, d._id)) {
          failed.push(`Deleting ${d.name} (${d.collection} ${d._id})`);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(`Deleting ${d.name} (${d.collection} ${d._id})`);
      }
      update("Deleting duplicates", ++done, deletes.length);
    }

    // 5. Check what's actually in the database now. The deleteObject lambda doesn't
    // await its deleteOne, so "deleted!" doesn't prove anything.
    try {
      update("Checking the results", done, deletes.length);
      const { full_docs, light_docs } = await loadSpellLists();
      const present = new Set([...full_docs.map(d => `spell_list ${d._id}`), ...light_docs.map(d => `spell_list_light ${d._id}`)]);
      const still_there = deletes.filter(d => present.has(`${d.collection} ${d._id}`));
      if (still_there.length > 0) {
        failed.push(`${still_there.length} deleted doc${still_there.length === 1 ? " is" : "s are"} still in the database (run it again to retry): ` +
          Array.from(new Set(still_there.map(d => d.name))).join(", "));
      }
      if (mounted.current) {
        setFulls(full_docs);
        setLights(light_docs);
        setKeepers({});
        setOrphanActions({});
        setRefDocs(prev => prev.map(r => {
          const t = run_targets.find(o => o.collection === r.collection && o.doc._id === r.doc._id);
          return t ? { ...r, doc: { ...r.doc, ...replaceIdRefs(r.doc, id_map).fields } } : r;
        }));
      }
    } catch (ex: any) {
      console.error(ex);
      failed.push(`Couldn't reload to check the results: ${ex?.message ?? ex}`);
    }
    notes.push("Reload the page before using the Spell List index, so it isn't showing what it loaded before the repair.");
    update("Done", done, deletes.length, true);
  };

  const renderPlan = (plan: Plan) => {
    const keeper_options = plan.fulls.map(f => {
      const refs = ref_counts.get(f._id) ?? 0;
      const books = docSBIds(f).map(sbName).join(", ");
      return { id: f._id, text: `${f.name} — created ${created(f._id)} — ${refs} ref${refs === 1 ? "" : "s"}${books ? ` — ${books}` : ""} — ${f._id}` };
    });
    const conflicts = mergeSpellListDocs([...plan.fulls, ...plan.lights],
      plan.fulls.find(f => f._id === plan.keeper_id) ?? plan.lights[0], sbName).conflicts;
    return (
      <Grid key={plan.key} size={12} style={{ borderBottom: "1px solid rgba(128,128,128,0.3)", paddingBottom: 8 }}>
        <div>
          <strong>{ plan.keeper_id ? <Link to={`/spell_list/${plan.keeper_id}`}>{ plan.name || "(unnamed)" }</Link> : (plan.name || "(unnamed)") }</strong>
          <span style={{ opacity: 0.7 }}>
            {" "}— { plan.fulls.length } full / { plan.lights.length } light cop{ plan.lights.length === 1 ? "y" : "ies" }
            { plan.link_groups > 1 ? ` from ${plan.link_groups} separate lists` : "" }
            {" "}— { plan.refs } ref{ plan.refs === 1 ? "" : "s" }
          </span>
        </div>
        { plan.fulls.length > 1 &&
          <SelectStringBox name="Keep" options={keeper_options} value={plan.keeper_id} disabled={running || confirm}
            onChange={(value: string) => setKeepers(prev => ({ ...prev, [plan.key]: value }))} />
        }
        { plan.fulls.length === 1 && <div style={{ opacity: 0.7 }}>Keeps { plan.keeper_id }, rebuilds its light copy.</div> }
        { plan.orphan_action &&
          <SelectStringBox name="No full doc left (probably one you deleted)" value={plan.orphan_action} disabled={running || confirm}
            options={[{ id: "delete", text: "Delete the leftover light copies" }, { id: "restore", text: "Restore it as a spell list" }]}
            onChange={(value: OrphanAction) => setOrphanActions(prev => ({ ...prev, [plan.key]: value }))} />
        }
        { plan.orphan_action === "delete" && plan.refs > 0 &&
          <div style={{ color: "orange" }}>{ plan.refs } reference{ plan.refs === 1 ? "" : "s" } will be left pointing at nothing.</div>
        }
        { conflicts.map(c => <div key={c} style={{ color: "orange" }}>{ c }</div>) }
      </Grid>
    );
  };

  const renderNameClusters = () => Array.from(name_clusters.entries())
    .filter(([, groups]) => groups.length > 1)
    .map(([name, groups]) => (
      <Grid key={name} size={12}>
        <CheckBox
          name={`Merge the ${groups.length} separate "${groups[0].name}" lists (${groups.map(g => groupSBKey(g).split(",").filter(s => s).map(sbName).join(", ") || "no books").join(" | ")})`}
          disabled={running || confirm}
          value={nameMerge(name, groups)}
          onChange={(value: boolean) => setNameMerges(prev => ({ ...prev, [name]: value }))}
        />
      </Grid>
    ));

  const renderAction = () => {
    if (status && running) {
      return <div>{ status.step }{ status.total > 0 ? ` ${status.done} / ${status.total}` : "" }...</div>;
    }
    if (confirm) {
      return (
        <>
          <div>
            Merge { work.length } spell list{ work.length === 1 ? "" : "s" }, repoint { targets.length } doc{ targets.length === 1 ? "" : "s" } that
            reference a duplicate, then delete the duplicates and old light copies? Everything touched downloads as a backup first
            (importable with Compass), and nothing changes if that fails.
            { dangling.length > 0 && <span style={{ color: "orange" }}> { dangling.length } deleted list{ dangling.length === 1 ? "" : "s" } still ha{ dangling.length === 1 ? "s" : "ve" } references.</span> }
          </div>
          <DandDButtonBox name="Download Backup, Then Repair" onClick={run} />
          <DandDButtonBox name="Cancel" onClick={() => setConfirm(false)} />
        </>
      );
    }
    return (
      <>
        <DandDButtonBox name={work.length > 0 ? `Repair ${work.length} Spell List${work.length === 1 ? "" : "s"}` : "Nothing to Repair"}
          disabled={work.length === 0} onClick={() => { setStatus(null); setConfirm(true); }} />
        { status &&
          <div>
            { status.failed.length > 0
              ? <div style={{ color: "red" }}>Problems: { status.failed.join("; ") }</div>
              : <div>Done.</div> }
            { status.notes.map(n => <div key={n} style={{ opacity: 0.8 }}>{ n }</div>) }
          </div>
        }
      </>
    );
  };

  return (
    <Grid container spacing={1} direction="row" className="padded">
      <Grid size={12} className="header centered">Repair Spell Lists</Grid>
      <Grid size={12} className="centered">
        { fulls.length } spell lists, { lights.length } light copies, { work.length } needing repair.
        { " " }{ all_ref_docs.length } docs scanned for references ({ targets.length } to repoint).
      </Grid>
      <Grid size={12}>
        Copies linked by id are always merged into one. Spells are combined per source book; any other limit setting
        comes from the newest copy. Then every reference to a removed copy is pointed at the kept one, and the
        duplicates and old light copies are deleted.
      </Grid>
      { skipped.length > 0 &&
        <Grid size={12} style={{ color: "orange" }}>
          Couldn&apos;t load { skipped.join(", ") }, so references in them won&apos;t be repointed.
        </Grid>
      }
      { renderNameClusters() }
      <Grid size={12} className="centered">
        { renderAction() }
      </Grid>
      { work.map(renderPlan) }
      <Grid size={12}>
        <CheckBox name="Show healthy lists" value={show_healthy} onChange={(value: boolean) => setShowHealthy(value)} />
      </Grid>
      { show_healthy && plans.filter(p => !needsWork(p)).map(p => (
        <Grid key={p.key} size={12}>
          <Link to={`/spell_list/${p.keeper_id}`}>{ p.name }</Link>
          <span style={{ opacity: 0.7 }}> — { p.refs } ref{ p.refs === 1 ? "" : "s" } — { p.keeper_id }</span>
        </Grid>
      ))}
    </Grid>
  );
}
